import apiClient, { getAuthToken } from './api';
import socketService from './socketService';
import { errorNotificationManager } from './errorNotificationService';
import { appNavigate } from './navigationService';
import i18n from '../i18n/config';
import { translateNotificationContent } from '../utils/notificationLocalization';
import { getNotificationPresentation } from '../hooks/useNotificationPreferences';

// Translate outside of React; respects the user's selected language.
const tn = (key: string, options?: Record<string, any>) => i18n.t(key, options);

/**
 * Unified Real-Time Notification Service
 * - WebSocket-based updates via SocketService
 * - Local persistence in `agency_notifications`
 * - Custom events: `notification-added` and `notifications-updated`
 * - Types: booking_update, approval/status_update, profile_update, etc.
 */
export interface Notification {
  id: string;
  type: 'approval' | 'rejection' | 'info_requested' | 'suspended' | 'reactivated' | 'status_update' | 'profile_update' | 'booking_update' | 'handover_delayed' | 'rental_ending' | 'collection_upcoming' | 'collection_overdue' | 'vehicle_maintenance' | 'vehicle_insurance';
  // Original backend notification type (e.g. NEW_BOOKING, COLLECTION_OVERDUE).
  // Preserved so the UI can localize the title/message from metadata; the
  // `type` field above is collapsed/remapped for icons & severity.
  contentType?: string;
  title: string;
  message: string;
  timestamp: Date;
  read: boolean;
  actionUrl?: string;
  bookingId?: string;
  bookingNumber?: string;
  severity?: 'info' | 'success' | 'warning' | 'error';
  actionable?: boolean;
  metadata?: Record<string, any>;
}

class NotificationService {
  private static unavailablePollingToken: string | undefined;
  private static STORAGE_KEY = 'agency_notifications';
  private static STATUS_KEY = 'last_checked_status';
  private static pollingTimer: number | null = null;
  private static pollingInFlight = false;
  private static pollingDelayMs = 30_000;
  private static readonly MIN_POLLING_DELAY_MS = 30_000;
  private static readonly MAX_POLLING_DELAY_MS = 2 * 60_000;
  private static isConnected = false;
  private static boundHandler = (p: any) => NotificationService.handleIncoming(p);
  private static boundVisibilityHandler = () => {
    if (document.visibilityState === 'visible' && NotificationService.pollingTimer) {
      NotificationService.scheduleNextPoll(0);
    }
  };
  private static boundOnlineHandler = () => {
    if (NotificationService.pollingTimer) {
      NotificationService.scheduleNextPoll(0);
    }
  };

  private static interpolateNotificationText(text: string, payload: any): string {
    if (!text || !payload) return text || '';

    return text
      .replace(/\{\{\s*bookingNumber\s*\}\}/g, payload.bookingNumber || '')
      .replace(/\{\{\s*bookingId\s*\}\}/g, payload.bookingId || '');
  }

  static connect(): void {
    // Prevent multiple subscriptions - only connect once
    if (this.isConnected) {
      return;
    }
    this.isConnected = true;

    socketService.connect();

    // Subscribe to socket events using a single bound handler
    const events = [
      'booking:pending',
      'booking:created',
      'booking:confirmed',
      'booking:active',
      'booking:completed',
      'booking:cancelled',
      'booking:overdue',
      'booking:daily-summary',
    ];
    events.forEach((event) => socketService.on(event, this.boundHandler));

    // Actionable alert events
    const alertEvents = [
      { event: 'booking:ending-soon', type: 'rental_ending' as const, severity: 'warning' as const },
      { event: 'handover:delayed', type: 'handover_delayed' as const, severity: 'warning' as const, actionable: true },
      { event: 'collection:upcoming', type: 'collection_upcoming' as const, severity: 'info' as const },
      { event: 'collection:overdue', type: 'collection_overdue' as const, severity: 'error' as const },
      { event: 'vehicle:oil_change', type: 'vehicle_maintenance' as const, severity: 'warning' as const },
      { event: 'vehicle:maintenance', type: 'vehicle_maintenance' as const, severity: 'warning' as const },
      { event: 'vehicle:insurance', type: 'vehicle_insurance' as const, severity: 'warning' as const },
    ];
    alertEvents.forEach(({ event, type, severity, actionable }) => {
      socketService.on(event, (payload: any) => {
        if (!payload) return;
        const title = this.interpolateNotificationText(payload.title || tn('errorHandling.status.alert'), payload);
        const message = this.interpolateNotificationText(payload.message || '', payload);
        const notification = {
          type,
          contentType: payload.type,
          title,
          message,
          actionUrl: payload.actionUrl,
          bookingId: payload.bookingId,
          bookingNumber: payload.bookingNumber,
          severity,
          actionable: actionable || false,
          metadata: payload.metadata || {},
        };
        this.addNotification(notification);

        const localized = translateNotificationContent(notification, tn);
        if (getNotificationPresentation().popups) errorNotificationManager.showInfo(localized.title, localized.message, {
          source: 'notification',
          actions: payload.actionUrl ? [{ label: tn('errorHandling.status.view'), onClick: () => appNavigate(payload.actionUrl), variant: 'contained' as const }] : [],
          duration: severity === 'error' ? 12000 : 7000,
        });
      });
    });

    // Handle reconnection polling logic if needed
    const socket = socketService.getSocket();
    if (socket) {
      socket.io.on('reconnect_attempt', (attempt: number) => {
        if (attempt >= 5 && !this.pollingTimer) {
          this.startPolling();
        }
      });

      socket.on('connect', () => {
        this.stopPolling();
      });
    }
  }

  private static handleIncoming(payload: any): void {
    if (!payload) return;
    const title = this.interpolateNotificationText(payload.title || tn('errorHandling.status.bookingUpdate'), payload);
    const message = this.interpolateNotificationText(payload.message || '', payload);
    const actionUrl = payload.actionUrl || `/pos/bookings/${payload.bookingId || ''}`;
    const notification = {
      type: 'booking_update',
      contentType: payload.type,
      title,
      message,
      actionUrl,
      bookingId: payload.bookingId,
      bookingNumber: payload.bookingNumber,
      metadata: payload.metadata || {},
    } as const;
    this.addNotification(notification);
    const localized = translateNotificationContent(notification, tn);
    const actions = [{ label: tn('errorHandling.status.open'), onClick: () => appNavigate(actionUrl), variant: 'contained' as const }];
    if (getNotificationPresentation().popups) errorNotificationManager.showInfo(localized.title, localized.message, { source: 'notification', actions, duration: 7000 });
  }

  private static async fetchServerNotifications(): Promise<{ items: any[]; ok: boolean; stop?: boolean }> {
    try {
      const response = await apiClient.get('/agency/notifications', { backgroundRequest: true } as any);
      // Handle both array responses and { data: [] } wrapped responses
      const data = response.data?.data || response.data;
      const items = Array.isArray(data) ? data : [];
      return { items, ok: true };
    } catch (e: any) {
      if ([401, 403, 404].includes(e?.response?.status)) {
        this.unavailablePollingToken = getAuthToken() || '';
        return { items: [], ok: false, stop: true };
      }
      // Retry temporary failures with backoff; WebSocket is the primary source.
      console.debug('[NotificationService] Polling fallback error:', e?.message);
      return { items: [], ok: false };
    }
  }

  private static startPolling(): void {
    if (this.unavailablePollingToken === (getAuthToken() || '')) return;
    if (this.pollingTimer) return;
    this.pollingDelayMs = this.MIN_POLLING_DELAY_MS;
    document.addEventListener('visibilitychange', this.boundVisibilityHandler);
    window.addEventListener('online', this.boundOnlineHandler);
    this.scheduleNextPoll(0);
  }

  private static stopPolling(): void {
    if (this.pollingTimer) {
      window.clearTimeout(this.pollingTimer);
      this.pollingTimer = null;
    }
    this.pollingInFlight = false;
    this.pollingDelayMs = this.MIN_POLLING_DELAY_MS;
    document.removeEventListener('visibilitychange', this.boundVisibilityHandler);
    window.removeEventListener('online', this.boundOnlineHandler);
  }

  private static scheduleNextPoll(delayMs = this.pollingDelayMs): void {
    if (this.pollingTimer) {
      window.clearTimeout(this.pollingTimer);
    }

    this.pollingTimer = window.setTimeout(() => {
      this.runPollingFallback();
    }, delayMs);
  }

  private static async runPollingFallback(): Promise<void> {
    if (socketService.getSocket()?.connected) {
      this.stopPolling();
      return;
    }

    if (document.visibilityState === 'hidden' || !navigator.onLine) {
      this.scheduleNextPoll(this.MAX_POLLING_DELAY_MS);
      return;
    }

    if (this.pollingInFlight) {
      this.scheduleNextPoll(this.pollingDelayMs);
      return;
    }

    this.pollingInFlight = true;
    const result = await this.fetchServerNotifications();
    this.pollingInFlight = false;
    if (result.stop) { this.stopPolling(); return; }

    for (const p of result.items) {
      this.handleIncoming(p);
    }

    this.pollingDelayMs = result.ok
      ? this.MIN_POLLING_DELAY_MS
      : Math.min(this.pollingDelayMs * 2, this.MAX_POLLING_DELAY_MS);
    this.scheduleNextPoll(this.pollingDelayMs);
  }

  static getSocket() {
    return socketService.getSocket();
  }

  // Get all notifications
  static getNotifications(): Notification[] {
    const stored = localStorage.getItem(this.STORAGE_KEY);
    if (!stored) return [];

    const parsed = JSON.parse(stored);
    return parsed.map((n: any) => ({
      ...n,
      timestamp: new Date(n.timestamp)
    }));
  }

  // Add a new notification
  static addNotification(notification: Omit<Notification, 'id' | 'timestamp' | 'read'>): void {
    const newNotification: Notification = {
      ...notification,
      id: `${notification.type}-${crypto.randomUUID()}`,
      timestamp: new Date(),
      read: false,
    };

    const notifications = this.getNotifications();
    const updated = [newNotification, ...notifications];
    localStorage.setItem(this.STORAGE_KEY, JSON.stringify(updated));

    // Trigger custom event for real-time updates
    window.dispatchEvent(new CustomEvent('notification-added', { detail: newNotification }));
  }

  // Mark notification as read
  static markAsRead(id: string): void {
    const notifications = this.getNotifications();
    const updated = notifications.map(n =>
      n.id === id ? { ...n, read: true } : n
    );
    localStorage.setItem(this.STORAGE_KEY, JSON.stringify(updated));
    window.dispatchEvent(new Event('notifications-updated'));
  }

  // Mark all as read
  static markAllAsRead(): void {
    const notifications = this.getNotifications();
    const updated = notifications.map(n => ({ ...n, read: true }));
    localStorage.setItem(this.STORAGE_KEY, JSON.stringify(updated));
    window.dispatchEvent(new Event('notifications-updated'));
  }

  // Clear notification
  static clearNotification(id: string): void {
    const notifications = this.getNotifications();
    const updated = notifications.filter(n => n.id !== id);
    localStorage.setItem(this.STORAGE_KEY, JSON.stringify(updated));
    window.dispatchEvent(new Event('notifications-updated'));
  }

  // Clear all notifications
  static clearAll(): void {
    localStorage.removeItem(this.STORAGE_KEY);
    window.dispatchEvent(new Event('notifications-updated'));
  }

  // Get unread count
  static getUnreadCount(): number {
    return this.getNotifications().filter(n => !n.read).length;
  }

  // Check for status changes and create notifications
  static checkStatusChanges(currentStatus: string, previousStatus?: string): void {
    const lastStatus = previousStatus || localStorage.getItem(this.STATUS_KEY);

    if (lastStatus && lastStatus !== currentStatus) {
      const notification = this.createStatusNotification(currentStatus);
      if (notification) {
        this.addNotification(notification);
      }
    }

    localStorage.setItem(this.STATUS_KEY, currentStatus);
  }

  // Create notification based on status
  private static createStatusNotification(status: string): Omit<Notification, 'id' | 'timestamp' | 'read'> | null {
    const agencyData = localStorage.getItem('agency');
    const agency = agencyData ? JSON.parse(agencyData) : {};

    switch (status) {
      case 'approved':
        return {
          type: 'approval',
          title: tn('errorHandling.status.approvedTitle'),
          message: tn('errorHandling.status.approvedMessage'),
          actionUrl: '/dashboard'
        };

      case 'rejected':
        return {
          type: 'rejection',
          title: tn('errorHandling.status.rejectedTitle'),
          message: agency.rejection_reason || tn('errorHandling.status.rejectedMessage'),
          actionUrl: '/profile'
        };

      case 'info_requested':
        return {
          type: 'info_requested',
          title: tn('errorHandling.status.infoRequestedTitle'),
          message: agency.info_request_details || tn('errorHandling.status.infoRequestedMessage'),
          actionUrl: '/profile/edit'
        };

      case 'suspended':
        return {
          type: 'suspended',
          title: tn('errorHandling.status.suspendedTitle'),
          message: agency.admin_notes || tn('errorHandling.status.suspendedMessage'),
          actionUrl: '/dashboard'
        };

      case 'under_review':
        return {
          type: 'status_update',
          title: tn('errorHandling.status.underReviewTitle'),
          message: tn('errorHandling.status.underReviewMessage'),
          actionUrl: '/dashboard'
        };

      case 'profile_incomplete':
        return {
          type: 'status_update',
          title: tn('errorHandling.status.profileIncompleteTitle'),
          message: tn('errorHandling.status.profileIncompleteMessage'),
          actionUrl: '/profile/edit'
        };

      case 'basic_info_required':
        return {
          type: 'status_update',
          title: tn('errorHandling.status.basicInfoTitle'),
          message: tn('errorHandling.status.basicInfoMessage'),
          actionUrl: '/onboarding'
        };

      default:
        return null;
    }
  }

  // Notify on profile update
  static notifyProfileUpdate(message: string = tn('errorHandling.status.profileUpdatedMessage')): void {
    this.addNotification({
      type: 'profile_update',
      title: tn('errorHandling.status.profileUpdatedTitle'),
      message,
      actionUrl: '/profile'
    });
  }

  // Notify on booking status change
  static notifyBookingStatusChange(isOpen: boolean): void {
    const title = isOpen
      ? tn('errorHandling.status.bookingsEnabledTitle')
      : tn('errorHandling.status.bookingsPausedTitle');
    const message = isOpen
      ? tn('errorHandling.status.bookingsEnabledMessage')
      : tn('errorHandling.status.bookingsPausedMessage');
    this.addNotification({
      type: 'booking_update',
      title,
      message,
      actionUrl: '/dashboard'
    });
    if (getNotificationPresentation().popups) errorNotificationManager.showInfo(
      title,
      message,
      { source: 'notification', actions: [{ label: tn('errorHandling.status.open'), onClick: () => appNavigate('/dashboard'), variant: 'contained' as const }] }
    );
  }

  // Notify custom message
  static notify(title: string, message: string, type: Notification['type'] = 'status_update', actionUrl?: string): void {
    this.addNotification({
      type,
      title,
      message,
      actionUrl
    });
  }
}

export default NotificationService;
export const initNotificationSocket = () => NotificationService.connect();
